import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ResponsiveContainer, Customized,
} from "recharts";
import { AlertTriangle, Coins, Loader2 } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";
import { useLiveDashboard } from "../hooks/useLiveDashboard";
import { formatCostValue } from "./AiIntelligenceCharts";

/**
 * AI spend the way the dashboard draws exposure (ExposureTrajectoryChart): settled actuals as a
 * solid line, the server's projection as a dashed tail with its day-level range behind it, and a
 * $ callout at the end of each. Self-contained — it owns both reads — so the Overview card and
 * the Usage & Cost tab's Spend mode behave identically.
 *
 * The module's disciplines apply here in full: an unsettled day draws nothing rather than a zero,
 * a missing forecast means no dashed line (the reason renders as a sentence — never a client-side
 * extrapolation), and currencies are never merged — every currency the ledger reports is its own
 * series (`cost_<CUR>`) built from `daily[].costs`, so a euro and a dollar never meet on one line,
 * one axis label, or one tooltip row. Mixed currencies drop the axis symbol (no symbol is right
 * there) and each end label carries its own.
 */

// The 7/30/90-day selector, shared with AiUsageCostPanel's Tokens chart so both read the same
// window labels. Lives here rather than in the panel because the panel mounts this chart.
export const PERIODS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

// teal-600 — the spend series colour shared with AiUsageCostPanel's Forecast subtab.
export const SPEND_COLOR = "#0d9488";
const PROJECTED_OPACITY = 0.7;

// Colours for the second and later currencies when an estate bills in several. A lone currency —
// any currency — keeps SPEND_COLOR so the common single-currency chart looks exactly as it did;
// USD keeps it in a mixed chart too. indigo-600, amber-600, rose-600, sky-600, violet-600.
const EXTRA_CURRENCY_COLORS = ["#4f46e5", "#d97706", "#e11d48", "#0284c7", "#7c3aed"];

// The series key for one currency's settled spend. Every plotted amount lives under one of these.
export const costKey = (currency) => `cost_${currency}`;

/**
 * One colour per currency: a single currency (whatever it is) draws in SPEND_COLOR; in a mixed
 * chart USD keeps SPEND_COLOR and the others take the fixed palette in currency order.
 */
export function currencyColors(currencies) {
  const colors = {};
  if (currencies.length <= 1) {
    currencies.forEach((c) => { colors[c] = SPEND_COLOR; });
    return colors;
  }
  let i = 0;
  currencies.forEach((c) => {
    if (c === "USD") { colors[c] = SPEND_COLOR; return; }
    colors[c] = EXTRA_CURRENCY_COLORS[i % EXTRA_CURRENCY_COLORS.length];
    i += 1;
  });
  return colors;
}

// Endpoint marker for one line. Draws the dot and, after commit, reports its Recharts-computed
// pixel position up to the chart so the collision-aware label layer can place every callout with
// knowledge of the others. Reports null on unmount so no stale label lingers. Module scope on
// purpose: an inline component type changes identity every parent render and would remount.
function EndDot({ seriesKey, cx, cy, color, opacity = 1, onPosition }) {
  useLayoutEffect(() => {
    onPosition(seriesKey, { cx, cy });
    return () => onPosition(seriesKey, null);
  }, [seriesKey, cx, cy, onPosition]);
  return (
    <circle
      data-testid={`spend-end-dot-${seriesKey}`}
      cx={cx} cy={cy} r={3.5} fill={color} fillOpacity={opacity} stroke="#fff" strokeWidth={1}
    />
  );
}

// Copied from ExposureTrajectoryChart: "$1.2M" / "$340K" / "$12".
function fmtMoney(val) {
  if (val == null) return "—";
  const n = Number(val);
  if (isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

// Axis ticks and end labels. USD keeps the dashboard's "$" form; another single currency gets its
// own compact symbol; a null currency (mixed) shows the bare number — no symbol is right there.
export function fmtCompactMoney(val, currency) {
  if (val == null) return "—";
  if (currency === "USD") return fmtMoney(val);
  if (!currency) return fmtMoney(val).replace(/^\$/, "");
  // Only the symbol comes from Intl: its compact-notation digits differ between ICU builds
  // ("€4" on one Node, "€4.0" on another), so the digits use the same rounding as fmtMoney.
  return `${currencySymbol(currency)}${fmtMoney(val).replace(/^\$/, "")}`;
}

const symbolCache = new Map();
function currencySymbol(currency) {
  if (symbolCache.has(currency)) return symbolCache.get(currency);
  let symbol = currency;
  try {
    const part = new Intl.NumberFormat("en-US", { style: "currency", currency })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    if (part?.value) symbol = part.value;
  } catch {
    // Unknown ISO code: fall back to the code itself.
  }
  symbolCache.set(currency, symbol);
  return symbol;
}

const fmtDay = (ts) =>
  new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
/**
 * Merge the settled actuals and the projection into one array keyed by epoch ms.
 *
 * Every actual day is a row carrying one `cost_<CUR>` key per currency in the window: the day's
 * amount in that currency, or null where the day did not bill in it (a gap, never a zero —
 * connectNulls is off). Amounts of different currencies are never added; the currency list is the
 * server's `currencies`, widened by anything a day's `costs` names so no amount is ever dropped.
 *
 * The projection is only merged when the server says it is available, and it belongs to one
 * currency (`forecast.currency`): the dashed line is anchored on the last settled point in THAT
 * currency so the two read as one series, and the range band (an invisible base area up to the
 * low bound plus a visible High−Low fill stacked on it — Recharts has no native band primitive)
 * tapers from that same point. No settled point in the forecast's currency means no tail.
 */
export function buildSpendSeries(usage, forecast, days) {
  const daily = usage?.daily || [];
  const seen = new Set(usage?.currencies || []);
  daily.forEach((d) => (d.costs || []).forEach((c) => { if (c?.currency) seen.add(c.currency); }));
  const currencies = Array.from(seen).sort();

  const byTs = new Map();
  const upsert = (ts, patch) => byTs.set(ts, { ...(byTs.get(ts) || { ts }), ...patch });

  daily.forEach((d) => {
    const ts = new Date(d.date).getTime();
    if (isNaN(ts)) return;
    const row = {};
    currencies.forEach((c) => { row[costKey(c)] = null; });
    (d.costs || []).forEach((c) => {
      if (c?.currency && c.amount != null) row[costKey(c.currency)] = c.amount;
    });
    upsert(ts, row);
  });

  const sorted = Array.from(byTs.values()).sort((a, b) => a.ts - b.ts);
  const lastActualByCurrency = {};
  currencies.forEach((c) => {
    const key = costKey(c);
    const last = [...sorted].reverse().find((r) => r[key] != null);
    if (last) lastActualByCurrency[c] = { ts: last.ts, value: last[key] };
  });

  const projectionCurrency = forecast?.available ? (forecast.currency || "USD") : null;
  const anchor = projectionCurrency ? lastActualByCurrency[projectionCurrency] || null : null;
  const projected = forecast?.available
    ? (forecast.dailyProjections || []).slice(0, days)
    : [];

  if (projected.length > 0 && anchor) {
    upsert(anchor.ts, { projected: anchor.value, projLow: anchor.value, projBand: 0 });
    for (const p of projected) {
      const ts = new Date(p.date).getTime();
      if (isNaN(ts) || ts <= anchor.ts) continue;
      upsert(ts, {
        projected: p.expectedUsd,
        projLow: p.lowUsd,
        projBand: p.highUsd - p.lowUsd,
        projHigh: p.highUsd,
      });
    }
  }

  const hasProjection = projected.length > 0 && !!anchor;
  return {
    rows: Array.from(byTs.values()).sort((a, b) => a.ts - b.ts),
    currencies,
    lastActualByCurrency,
    anchorTs: anchor?.ts ?? null,
    projectionCurrency: hasProjection ? projectionCurrency : null,
    hasProjection,
  };
}

// The currencies a row carries, in code order — lets the tooltip stand alone in its unit test.
const rowCurrencies = (row) =>
  Object.keys(row).filter((k) => k.startsWith("cost_")).map((k) => k.slice(5)).sort();

/**
 * Date, one settled row per currency the day billed in (none → "Not reported"), and the
 * projection with its range where one exists. Reads the row itself rather than the payload
 * entries so the band's internal keys (projLow/projBand) never surface as rows a person reads.
 * A single currency labels its row "Spend"; several label each row with its currency code, and
 * each amount is formatted in its own currency — never one symbol over another's figure.
 */
export function SpendTooltip({ active, payload, label, currencies, colors, projectionCurrency }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const curs = currencies?.length ? currencies : rowCurrencies(row);
  const colorOf = (c) => colors?.[c] || SPEND_COLOR;
  const isActualDay = curs.some((c) => Object.prototype.hasOwnProperty.call(row, costKey(c)));
  const present = curs.filter((c) => row[costKey(c)] != null);
  const mixed = curs.length > 1;
  const projCur = projectionCurrency || curs[0] || null;
  // The anchor row carries the projection only to join the two lines; a person reads it as the
  // settled figure. An unsettled day that also has a projection shows both, honestly.
  const showProjection = row.projected != null && (!projCur || row[costKey(projCur)] == null);
  return (
    <div className="bg-theme-card-solid border border-border rounded-lg shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-content-primary mb-1">{fmtDay(label ?? row.ts)}</div>
      {isActualDay && present.length === 0 && (
        <div className="flex items-center justify-between gap-3" style={{ color: SPEND_COLOR }}>
          <span>Spend</span>
          <span className="font-semibold">{formatCostValue(null, curs[0] || null)}</span>
        </div>
      )}
      {present.map((c) => (
        <div key={c} className="flex items-center justify-between gap-3" style={{ color: colorOf(c) }}>
          <span>{mixed ? c : "Spend"}</span>
          <span className="font-semibold">{formatCostValue(row[costKey(c)], c)}</span>
        </div>
      ))}
      {showProjection && (
        <>
          <div className="flex items-center justify-between gap-3" style={{ color: colorOf(projCur), opacity: PROJECTED_OPACITY }}>
            <span>Projected</span>
            <span className="font-semibold">{formatCostValue(row.projected, projCur)}</span>
          </div>
          {row.projLow != null && row.projHigh != null && (
            <div className="flex items-center justify-between gap-3 text-content-secondary">
              <span>Range</span>
              <span>{formatCostValue(row.projLow, projCur)} – {formatCostValue(row.projHigh, projCur)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Header({ compact, refreshing, badge, children }) {
  return (
    <div className={`flex items-center justify-between gap-2 flex-wrap ${compact ? "mb-2" : "mb-3"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Coins className="w-4 h-4 text-teal-700 shrink-0" />
        <span className="text-sm font-semibold text-content-primary">Spend trajectory</span>
        {refreshing && <Loader2 className="w-3.5 h-3.5 text-content-secondary animate-spin" aria-label="Refreshing" />}
        {badge && (
          <span className="text-[11px] text-content-secondary bg-theme-hover rounded px-1.5 py-0.5 whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function PeriodSelector({ days, onChange, compact, disabled }) {
  return (
    <div className="flex items-center gap-1.5">
      {!compact && <span className="text-[11px] text-content-secondary">Range</span>}
      <div className="flex items-center gap-1" role="group" aria-label="Period">
        {PERIODS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => onChange(p.days)}
            disabled={disabled}
            aria-pressed={days === p.days}
            className={`px-2 py-0.5 text-[11px] rounded border ${
              days === p.days
                ? "border-border bg-theme-hover text-content-primary font-medium"
                : "border-transparent text-content-secondary hover:bg-theme-hover"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SpendTrajectorySkeleton({ height = 210, compact = false }) {
  return (
    <Card>
      <div className={`flex items-center gap-2 ${compact ? "mb-2" : "mb-3"}`}>
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
      </div>
      <div
        className="bg-gray-100 rounded animate-pulse flex flex-col items-center justify-center gap-2"
        style={{ height }}
      >
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        <span className="text-xs text-gray-400">Loading spend data…</span>
      </div>
    </Card>
  );
}

// A failure to read the ledger, said as such — never "no spend". Distinct from the empty state so
// a dropped request cannot masquerade as a clean bill of health about money.
export function SpendTrajectoryError({ message, onRetry, height = 210, compact = false }) {
  return (
    <Card>
      <Header compact={compact} />
      <div className="flex items-center justify-center text-center px-4" style={{ height }}>
        <div>
          <div className="flex items-center justify-center gap-2 text-xs text-amber-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Spend could not be loaded{message ? ` — ${message}` : ""}.</span>
          </div>
          <p className="text-[11px] text-content-secondary mt-1">
            This is a failure to read the ledger, not a report that there was no spend.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 px-2.5 py-1 text-xs rounded-md border border-border hover:bg-theme-hover text-content-primary"
          >
            Retry
          </button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Range control, two modes. Uncontrolled (the Overview card): `initialDays` seeds an internal
 * window and the chart's own 7/30/90 selector changes it. Controlled (the Usage & Cost tab): the
 * host passes `days` and `onDaysChange`, the chart refetches whenever `days` changes, and with
 * `showSelector={false}` it renders no selector of its own — one control on the page drives both
 * the tokens chart and this one, rather than two pickers where one is inert.
 */
export default function SpendTrajectoryChart({
  orgId, initialDays = 30, days: controlledDays, onDaysChange, showSelector = true, compact = false, height = 210,
}) {
  const apiCall = useApi();
  const [internalDays, setInternalDays] = useState(initialDays);
  const controlled = controlledDays != null;
  const days = controlled ? controlledDays : internalDays;
  const setDays = useCallback((d) => {
    if (!controlled) setInternalDays(d);
    if (onDaysChange) onDaysChange(d);
  }, [controlled, onDaysChange]);
  const [usage, setUsage] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [usageSettled, setUsageSettled] = useState(false);
  const [forecastSettled, setForecastSettled] = useState(false);
  // `refreshing` flips on for every usage fetch after the first paint (a range change, a live
  // refresh, a Retry) so the chart never drops back to the full skeleton mid-interaction.
  const [refreshing, setRefreshing] = useState(false);
  // A failed fetch must never erase data that already loaded: keep the last payload and warn.
  const [fetchFailed, setFetchFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // Guards a slow response for the previous range from overwriting the newer one.
  const usageRequestSeq = useRef(0);

  const fetchUsage = useCallback(() => {
    const seq = ++usageRequestSeq.current;
    setRefreshing(true);
    return apiCall("GET", `/ai-assets/usage?days=${days}`)
      .then((d) => {
        if (seq !== usageRequestSeq.current) return;
        setUsage(d ?? null);
        setFetchFailed(false);
        setErrorMessage(null);
      })
      .catch((e) => {
        if (seq !== usageRequestSeq.current) return;
        setFetchFailed(true);
        setErrorMessage(e?.response?.data?.message || e?.message || null);
      })
      .finally(() => {
        if (seq !== usageRequestSeq.current) return;
        setUsageSettled(true);
        setRefreshing(false);
      });
  }, [apiCall, days]);

  // The projection is an opinion about the actuals; it going missing must not take the
  // measurement with it, so its failure is swallowed into "no projection" rather than an error.
  const fetchForecast = useCallback(() => {
    return apiCall("GET", "/ai-assets/usage/forecast")
      .then((f) => setForecast(f ?? null))
      .catch(() => setForecast(null))
      .finally(() => setForecastSettled(true));
  }, [apiCall]);

  // Both reads start together; the usage read re-runs when the range changes, the forecast once.
  // orgId is an effect dependency on purpose: the header comes from localStorage inside useApi,
  // so a host that switches org must trigger a re-read even though the URL is unchanged.
  useEffect(() => { fetchUsage(); }, [fetchUsage, orgId]);
  useEffect(() => { fetchForecast(); }, [fetchForecast, orgId]);

  const refreshAll = useCallback(() => {
    fetchUsage();
    fetchForecast();
  }, [fetchUsage, fetchForecast]);

  useLiveDashboard({ refresh: refreshAll });

  const series = useMemo(() => buildSpendSeries(usage, forecast, days), [usage, forecast, days]);
  // The axis wears a symbol only when every figure on it is in one currency. Null means "these
  // span several" and the ticks show bare numbers; with nothing priced yet the server's headline
  // currency keeps the axis as it was.
  const axisCurrency = series.currencies.length > 1
    ? null
    : (series.currencies[0] || usage?.currency || null);
  // The YAxis tickFormatter MUST keep its identity between renders (useCallback here, the same
  // way ExposureTrajectoryChart passes the module-level fmtMoney). Recharts 3 registers the
  // axis's props in its store and a new tickFormatter function replaces that registration,
  // which recomputes every line's points array and re-keys the line's dots (AnimatedItems is
  // keyed on the points reference), unmounting and remounting EndDot in one commit. EndDot's
  // cleanup reports null and its remount re-reports the position — a state change per commit —
  // and the re-render that follows creates the next new formatter. An inline arrow here loops
  // to "Maximum update depth exceeded" the moment the chart has a settled point; reproduced
  // against the unmocked ResponsiveContainer (see SpendTrajectoryChart.test.js).
  const yTickFormatter = useCallback((v) => fmtCompactMoney(v, axisCurrency), [axisCurrency]);

  // Endpoint pixel positions for the end-of-line $ labels — declared HERE, above every early
  // return, because hooks must run in the same order on every render (React #310 otherwise; this
  // exact chart family shipped that bug to prod once — see ExposureTrajectoryChart).
  const endDotPositionsRef = useRef({});
  const [endDotPositions, setEndDotPositions] = useState({});
  const reportEndDot = useCallback((seriesKey, pos) => {
    const current = endDotPositionsRef.current;
    const prev = current[seriesKey];
    if (pos == null) {
      if (!prev) return;
      const { [seriesKey]: _removed, ...rest } = current;
      endDotPositionsRef.current = rest;
      setEndDotPositions(rest);
      return;
    }
    if (prev && prev.cx === pos.cx && prev.cy === pos.cy) return;
    endDotPositionsRef.current = { ...current, [seriesKey]: pos };
    setEndDotPositions(endDotPositionsRef.current);
  }, []);

  // ── Early returns: nothing below this line may declare a hook ─────────────────────────────
  const loaded = usageSettled && forecastSettled;
  if (!loaded) return <SpendTrajectorySkeleton height={height} compact={compact} />;
  if (!usage && fetchFailed) {
    return <SpendTrajectoryError message={errorMessage} onRetry={refreshAll} height={height} compact={compact} />;
  }

  const badge = usage?.hasData ? `${usage.daysWithData} of ${usage.daysRequested} days recorded` : null;
  const selector = showSelector ? (
    <PeriodSelector days={days} onChange={setDays} compact={compact} disabled={refreshing} />
  ) : null;

  // An empty ledger is stated in the server's own words — the feed knows whether it never ran
  // or whether there was nothing to read, and this card must not guess between them.
  if (!usage?.hasData) {
    return (
      <Card>
        <Header compact={compact} refreshing={refreshing}>{selector}</Header>
        <div className="flex items-center justify-center text-center px-4" style={{ height }}>
          <p className="text-xs text-content-secondary">
            {usage?.coverageWarning || "No spend reported for this period."}
          </p>
        </div>
      </Card>
    );
  }

  const { rows, currencies, lastActualByCurrency, anchorTs, projectionCurrency, hasProjection } = series;
  const mixed = currencies.length > 1;
  const colors = currencyColors(currencies);

  const lastProjected = hasProjection
    ? [...rows].reverse().find((r) => r.projected != null && r[costKey(projectionCurrency)] == null)
    : null;

  // End-of-line callouts: the last settled point of every currency and the last projected point.
  // Each Line's `dot` callback draws only its endpoint circle and reports cx/cy into state; the
  // single <Customized> layer below reads every position and pushes colliding labels apart.
  const endLabelByKey = {};
  currencies.forEach((c) => {
    const last = lastActualByCurrency[c];
    if (!last) return;
    const key = costKey(c);
    endLabelByKey[key] = { key, color: colors[c], opacity: 1, value: last.value, ts: last.ts, currency: c };
  });
  if (lastProjected) {
    endLabelByKey.projected = {
      key: "projected", color: colors[projectionCurrency] || SPEND_COLOR, opacity: PROJECTED_OPACITY,
      value: lastProjected.projected, ts: lastProjected.ts, currency: projectionCurrency,
    };
  }

  function renderEndDot(key) {
    return (dotProps) => {
      const spec = endLabelByKey[key];
      if (!spec || dotProps.value == null || dotProps.payload?.ts !== spec.ts) return null;
      return (
        <EndDot
          key={`end-${key}`} seriesKey={key} cx={dotProps.cx} cy={dotProps.cy}
          color={spec.color} opacity={spec.opacity} onPosition={reportEndDot}
        />
      );
    };
  }

  // One pass over all end labels: ideal position just above-right of the label's own dot; where
  // two ideals collide the lower label is pushed down only as far as needed. Each label is
  // formatted in its own currency ("€1.2K" beside "$900") — the one place a mixed chart shows a
  // symbol, because here the figure and the currency are never apart.
  function EndLabelsLayer() {
    const MIN_LABEL_GAP = 13;
    const entries = Object.entries(endDotPositions)
      .filter(([key]) => endLabelByKey[key])
      .map(([key, pos]) => ({ ...endLabelByKey[key], ...pos, labelY: pos.cy - 5 }))
      .sort((a, b) => a.cy - b.cy);
    for (let i = 1; i < entries.length; i++) {
      if (entries[i].labelY < entries[i - 1].labelY + MIN_LABEL_GAP) {
        entries[i].labelY = entries[i - 1].labelY + MIN_LABEL_GAP;
      }
    }
    return (
      <g>
        {entries.map((e) => (
          <text
            key={`end-label-${e.key}`} data-testid={`spend-end-label-${e.key}`}
            x={e.cx + 7} y={e.labelY} textAnchor="start"
            fontSize={11} fontWeight={700} fill={e.color} fillOpacity={e.opacity}
            paintOrder="stroke" stroke="#fff" strokeWidth={4} strokeLinejoin="round"
          >
            {fmtCompactMoney(e.value, e.currency)}
          </text>
        ))}
      </g>
    );
  }

  const projectionNote = !hasProjection
    ? (forecast
        ? (forecast.unavailableReason || forecast.reason || "No spend projection is available for this period.")
        : "Spend projection could not be loaded.")
    : null;

  const projectedColor = colors[projectionCurrency] || SPEND_COLOR;

  return (
    <Card>
      <Header compact={compact} refreshing={refreshing} badge={badge}>{selector}</Header>

      {fetchFailed && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-2">
          Latest refresh failed — showing the last successfully loaded data.
        </p>
      )}

      <ResponsiveContainer width="100%" height={height}>
        {/* right: 68 leaves room for a "$XXX.XK" callout beside the last point; top: 24 keeps an
            upward-trending label clear of the SVG edge. */}
        <ComposedChart data={rows} margin={{ top: 24, right: 68, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={fmtDay}
            tick={{ fontSize: 10 }}
            minTickGap={24}
          />
          <YAxis
            tickFormatter={yTickFormatter}
            tick={{ fontSize: 10 }}
            width={55}
          />
          <Tooltip
            wrapperStyle={{ zIndex: 50 }}
            content={(props) => (
              <SpendTooltip
                {...props} currencies={currencies} colors={colors} projectionCurrency={projectionCurrency}
              />
            )}
          />
          {/* The legend exists only when there is something to tell apart: one entry per currency,
              explicit so the band and the dashed tail never appear as entries. */}
          {mixed && (
            <Legend
              iconType="plainline"
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              payload={currencies.map((c) => ({ id: c, value: c, type: "plainline", color: colors[c] }))}
            />
          )}
          {hasProjection && anchorTs != null && (
            <ReferenceLine
              x={anchorTs}
              stroke="#94a3b8"
              strokeDasharray="4 2"
              label={{ value: "Today", position: "insideBottomRight", fontSize: 9, fill: "#64748b" }}
            />
          )}
          {/* Range band: invisible base up to the low bound, visible High−Low fill stacked on it.
              Drawn before the lines so they paint on top of the fill. */}
          {hasProjection && (
            <>
              <Area
                type="natural" dataKey="projLow" stackId="projection" stroke="none"
                fill={projectedColor} fillOpacity={0} isAnimationActive={false} legendType="none" connectNulls={false}
              />
              <Area
                type="natural" dataKey="projBand" stackId="projection" stroke="none"
                fill={projectedColor} fillOpacity={0.18} isAnimationActive={false} legendType="none" connectNulls={false}
              />
            </>
          )}
          {/* One settled line per currency; a day without that currency is a gap. Animation off:
              a dashed Line animates by redrawing its path, and a screenshot inside that window
              reads as a broken chart. */}
          {currencies.map((c) => (
            <Line
              key={costKey(c)}
              type="natural" dataKey={costKey(c)} name={mixed ? c : "Spend"}
              stroke={colors[c]} strokeWidth={1.5}
              dot={renderEndDot(costKey(c))} connectNulls={false} isAnimationActive={false}
            />
          ))}
          {hasProjection && (
            <Line
              type="natural" dataKey="projected" name="Projected"
              stroke={projectedColor} strokeOpacity={PROJECTED_OPACITY} strokeWidth={1.5} strokeDasharray="5 3"
              dot={renderEndDot("projected")} connectNulls={false} isAnimationActive={false}
            />
          )}
          {/* Rendered last so the labels paint on top of every line. */}
          <Customized component={EndLabelsLayer} />
        </ComposedChart>
      </ResponsiveContainer>

      {projectionNote && (
        <p className="text-[11px] text-content-secondary mt-1">{projectionNote}</p>
      )}

      {/* The one warning that must not be glanceable-away: a failed provider read makes this
          curve a floor, not a total. */}
      {usage.coverageWarning && (
        <div className="mt-1 text-[11px] text-amber-700">{usage.coverageWarning}</div>
      )}
      {(usage.incompleteDayCount > 0 || usage.provisionalDayCount > 0) && (
        <div className="mt-1 text-[11px] text-content-secondary">
          {usage.incompleteDayCount > 0 && (
            <span>
              {usage.incompleteDayCount} day{usage.incompleteDayCount === 1 ? " is" : "s are"} excluded — the
              provider read did not cover the whole day.
            </span>
          )}
          {usage.incompleteDayCount > 0 && usage.provisionalDayCount > 0 && " "}
          {usage.provisionalDayCount > 0 && (
            <span>
              {usage.provisionalDayCount} recent day{usage.provisionalDayCount === 1 ? "" : "s"} may still be
              restated by the provider.
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
